import { useTranslation } from 'react-i18next';

import type { CoverageTally } from './compare-grid.types';

interface CoverageMeterProps {
  tally: CoverageTally;
  /** Clarifies the denominator — counts cover the loaded page, not the whole lot. */
  scopeNote?: string;
  /** Automation hook: root `data-testid`; the two percentages get `-before-value` / `-now-value`. */
  testId?: string;
}

/** Percentage of `count` out of `total`, guarding the zero-row case. */
function pct(count: number, total: number): number {
  if (total <= 0) return 0;
  return Math.round((count / total) * 100);
}

/**
 * Two-tone field-coverage meter: the muted portion is what the feed already
 * carried, the primary portion is where the catalogue stands after enrichment.
 * Reads `53% → 100%` so the gain is legible without doing the arithmetic.
 */
export function CoverageMeter({ tally, scopeNote, testId = 'coverage-meter' }: CoverageMeterProps) {
  const { t } = useTranslation('content-enrichment');
  const beforePct = pct(tally.before, tally.total);
  const nowPct = pct(tally.now, tally.total);

  return (
    <div className="flex items-center gap-2.5" data-testid={testId}>
      <span className="text-muted-foreground text-xs font-medium">
        {t('compareGrid.coverage.label')}
      </span>
      <span
        className="bg-muted relative h-1.5 w-24 overflow-hidden rounded-sm"
        role="img"
        aria-label={t('compareGrid.coverage.ariaLabel', { before: beforePct, now: nowPct })}
        title={scopeNote}
      >
        {/* Enriched total sits underneath, feed baseline paints over its left portion. */}
        <span
          aria-hidden="true"
          className="bg-primary absolute top-0 left-0 h-full rounded-sm"
          style={{ width: `${String(nowPct)}%` }}
        />
        <span
          aria-hidden="true"
          className="bg-muted-foreground/40 absolute top-0 left-0 h-full rounded-sm"
          style={{ width: `${String(beforePct)}%` }}
        />
      </span>
      <span
        className="text-muted-foreground text-xs tabular-nums"
        data-testid={`${testId}-before-value`}
      >
        {beforePct}%
      </span>
      <span aria-hidden="true" className="text-muted-foreground text-xs">
        →
      </span>
      <span
        className="text-foreground text-xs font-semibold tabular-nums"
        data-testid={`${testId}-now-value`}
      >
        {nowPct}%
      </span>
    </div>
  );
}
